/**
 * Single source of truth for the document-upload size cap. The backend enforces
 * it on the presigned-upload endpoint; both UIs use it to tell the user what the
 * limit is when a PDF is rejected as too large.
 */
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;

export const DOCUMENT_MAX_MB = Math.round(DOCUMENT_MAX_BYTES / (1024 * 1024));

/**
 * Marker both UIs put on the pre-upload byte guard and the backend puts on its
 * 413 payloads, so every oversize rejection can be recognised and answered with
 * the same user-facing copy instead of a raw English server string.
 */
export const DOCUMENT_TOO_LARGE_ERROR_CODE = 'DOCUMENT_TOO_LARGE';

interface MaybeTooLargeError {
  code?: unknown;
  status?: unknown;
  statusCode?: unknown;
  message?: unknown;
  error?: {code?: unknown; statusCode?: unknown; message?: unknown} | unknown;
}

/**
 * True for every "document exceeds the byte cap" rejection: the client-side
 * guard in either UI, and the backend's 413 on the presign / preflight /
 * convert endpoints (Angular `HttpErrorResponse.status`, api-client
 * `ApiError.status`, or a nested Nest error body).
 */
export function isDocumentTooLargeError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const err = error as MaybeTooLargeError;
  const nested = (err.error && typeof err.error === 'object' ? err.error : {}) as {
    code?: unknown;
    statusCode?: unknown;
    message?: unknown;
  };

  if (err.code === DOCUMENT_TOO_LARGE_ERROR_CODE || nested.code === DOCUMENT_TOO_LARGE_ERROR_CODE) {
    return true;
  }
  if (err.status === 413 || err.statusCode === 413 || nested.statusCode === 413) {
    return true;
  }
  const messages = [err.message, nested.message];
  return messages.some((m) => typeof m === 'string' && m.includes(DOCUMENT_TOO_LARGE_ERROR_CODE));
}

const DOCUMENT_FORMAT_GLYPHS = {
  pdf: 'smap-format-pdf',
  docx: 'smap-format-doc',
  pptx: 'smap-format-ppt',
  txt: 'smap-format-txt',
  md: 'smap-format-md',
} as const;

export type DocumentFormatGlyph = (typeof DOCUMENT_FORMAT_GLYPHS)[keyof typeof DOCUMENT_FORMAT_GLYPHS] | 'smap-format-any';

export function documentFormatGlyph(fileName: string): DocumentFormatGlyph {
  const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
  return DOCUMENT_FORMAT_GLYPHS[extension as keyof typeof DOCUMENT_FORMAT_GLYPHS] ?? 'smap-format-any';
}
